import React, { createContext, useCallback, useEffect, useRef, useState } from "react";
import { DepartmentRequestType, DepartmentResponseType } from "./departmentContext";
import { clientFetch } from "../utils/clientFetch";
import { ContextFetchResult } from "../types/contextFetchResult";

const DEFAULT_COMPANY_LIST_LIMIT = 8;
const RESPONSE_MESSAGE_KEY = "message";
const DEFAULT_COMPANY_REQUEST_ERROR_MESSAGE = "企業情報の保存に失敗しました。";

type CompanyRequestError = Error & {
  status?: number;
  responseMessage?: string | null;
  errorCode?: string | null;
};

type DeviceType = {
  id: number,
  name: string,
  location: string,
  api_key: string,
  created_at: string,
  updated_at: string,
  created_by: string,
  updated_by: string
}

export type CompanyRequestType = {
  company_name?: string | null;
  address?: string | null;
  phone_number?: string | null;
  post_code?: number | null;
  toyoshima_sales_department?: string | null;
  toyoshima_sales_rep_name?: string | null;
  company_memo?: string | null;
  planned_site_name?: string | null;
  planned_site_address?: string | null;
  planned_site_phone?: number | null;
  planned_site_post_code?: number | null;
  planned_site_email?: string | null;
  planned_site_contact_name?: string | null;
  planned_site_contact_position?: string | null;
  contact_department?: string | null;
  contact_name?: string | null;
  contact_position?: string | null;
  contact_email?: string | null;
  contact_phone?: string | null;
  billing_department?: string | null;
  billing_contact?: string | null;
  billing_position?: string | null;
  billing_email?: string | null;
  billing_phone?: string | null;
  contract_start_date?: string | null;
  contract_end_date?: string | null;
  contract_amount?: number | null;
  departments?: DepartmentRequestType[] | null;
}

type CompanyDepartmentsResponse =
  | Partial<DepartmentResponseType>[]
  | {
    data: Partial<DepartmentResponseType>[];
    total: number;
    page: number;
    last_page: number;
  };

export type CompanyResponseType = {
  id: number,
  company_name: string,
  address: string,
  phone_number: string,
  post_code: number,
  toyoshima_sales_department: string,
  toyoshima_sales_rep_name: string,
  company_memo: string,
  planned_site_name: string,
  planned_site_address: string,
  planned_site_phone: number,
  planned_site_post_code: number,
  planned_site_email: string,
  planned_site_contact_name: string,
  planned_site_contact_position: string,
  contact_department?: string,
  contact_name?: string,
  contact_position?: string,
  contact_email?: string,
  contact_phone?: string,
  billing_department?: string,
  billing_contact?: string,
  billing_position?: string,
  billing_email?: string,
  billing_phone?: string,
  contract_start_date?: string,
  contract_end_date?: string,
  contract_amount?: number,
  created_at?: string,
  updated_at?: string,
  created_by?: string,
  updated_by?: string,
  is_disabled?: boolean,
  departments?: CompanyDepartmentsResponse,
  devices?: DeviceType[],
}

type CompanyListQuery = {
  page: number;
  limit: number;
};

type CompanyPaginationState = {
  total: number;
  page: number;
  last_page: number;
  limit: number;
};

async function parseErrorResponse(response: Response): Promise<{ message: string | null; errorCode: string | null }> {
  try {
    const contentType = response.headers.get("content-type") ?? "";
    if (!contentType.includes("application/json")) {
      return { message: null, errorCode: null };
    }

    const data = await response.json() as { message?: unknown; error_code?: unknown };
    const message = typeof data.message === "string" && data.message.length > 0 ? data.message : null;
    const errorCode = typeof data.error_code === "string" && data.error_code.length > 0 ? data.error_code : null;
    return { message, errorCode };
  } catch (error) {
    console.error("企業APIのエラーレスポンス解析に失敗しました。", error);
    return { message: null, errorCode: null };
  }
}

async function createCompanyRequestError(response: Response) {
  const { message: responseMessage, errorCode } = await parseErrorResponse(response);
  const error = new Error(responseMessage ?? DEFAULT_COMPANY_REQUEST_ERROR_MESSAGE) as CompanyRequestError;
  error.status = response.status;
  error.responseMessage = responseMessage;
  error.errorCode = errorCode;
  return error;
}

export const CompanyContext = createContext<{
  companies: CompanyResponseType[]
  company: CompanyResponseType | null
  companyPagination: CompanyPaginationState
}>({
  companies: [],
  company: null,
  companyPagination: {
    total: 0,
    page: 1,
    last_page: 1,
    limit: DEFAULT_COMPANY_LIST_LIMIT,
  },
})

export const UpdateCompanyContext = createContext<{
  refreshCompanies: (params?: Partial<CompanyListQuery>) => Promise<void>
  registerCompany: (value: CompanyRequestType) => Promise<void>
  updateCompany: (id: string, value: CompanyRequestType) => Promise<void>
  deleteCompany: (id: number) => Promise<void>
  refreshCompany: (id: string) => Promise<ContextFetchResult<CompanyResponseType>>
  toggleCompanyStatus: (id: number) => Promise<void>
}>({
  refreshCompanies: async (_params?: Partial<CompanyListQuery>) => { },
  registerCompany: async (value: CompanyRequestType) => { },
  updateCompany: async (id: string, value: CompanyRequestType) => { },
  deleteCompany: async (id: number) => { },
  refreshCompany: async (_id: string) => ({ status: null, data: null }),
  toggleCompanyStatus: async (id: number) => { },
})

export function useCompanyContext() {
  const [companies, setCompanies] = useState<CompanyResponseType[]>([])
  const [company, setCompany] = useState<CompanyResponseType | null>(null)
  const [companyPagination, setCompanyPagination] = useState<CompanyPaginationState>({
    total: 0,
    page: 1,
    last_page: 1,
    limit: DEFAULT_COMPANY_LIST_LIMIT,
  })
  const companyQueryRef = useRef<CompanyListQuery>({ page: 1, limit: DEFAULT_COMPANY_LIST_LIMIT })
  // 企業一覧取得
  const refreshCompanies = useCallback(async (params: Partial<CompanyListQuery> = {}) => {
    const nextQuery = { ...companyQueryRef.current, ...params }
    companyQueryRef.current = nextQuery

    try {
      const query = new URLSearchParams({
        page: String(nextQuery.page),
        limit: String(nextQuery.limit),
      })
      const endpoint = `${process.env.REACT_APP_API_ENDPOINT}/apis/admin/companies?${query.toString()}`

      const res = await clientFetch(endpoint, {
        headers: { "Content-Type": 'application/json' },
      })

      if (!res.ok) {
        throw new Error('Network response was not ok')
      }

      const data = await res.json()

      if (Array.isArray(data)) {
        setCompanies(data)
        setCompanyPagination({
          total: data.length,
          page: nextQuery.page,
          last_page: 1,
          limit: nextQuery.limit,
        })
      } else {
        const list = data?.data ?? []
        const total = data?.total ?? 0
        const page = data?.page ?? nextQuery.page
        const lastPage = data?.last_page ?? (nextQuery.limit > 0 ? Math.max(1, Math.ceil(total / nextQuery.limit)) : 1)

        setCompanies(list)
        setCompanyPagination({
          total,
          page,
          last_page: lastPage,
          limit: nextQuery.limit,
        })
      }
    } catch (error) {
      console.error('Error fetching companies list.', error)
    }
  }, [])

  // 企業登録
  const registerCompany = useCallback(async (value: CompanyRequestType) => {
    try {

      const res = await clientFetch(`${process.env.REACT_APP_API_ENDPOINT}/apis/admin/companies`, {
        method: 'POST',
        headers: { "Content-Type": 'application/json' },
        body: JSON.stringify(value),
      })

      if (!res.ok) {
        throw await createCompanyRequestError(res)
      }

      await refreshCompanies()
    } catch (error) {
      console.error("企業登録に失敗しました。", error);
      throw error;
    }
  }, [refreshCompanies])

  // 企業IDで企業編集
  const updateCompany = useCallback(async (id: string, value: CompanyRequestType) => {
    try {

      const res = await clientFetch(`${process.env.REACT_APP_API_ENDPOINT}/apis/admin/companies/${id}`, {
        method: 'PUT',
        headers: { "Content-Type": 'application/json' },
        body: JSON.stringify(value),
      })

      if (!res.ok) {
        throw await createCompanyRequestError(res)
      }

      await refreshCompanies()
    } catch (error) {
      console.error('企業更新に失敗しました。', error)
      throw error
    }
  }, [refreshCompanies])

  // 企業IDで詳細取得
  const refreshCompany = useCallback(async (id: string): Promise<ContextFetchResult<CompanyResponseType>> => {
    try {

      const res = await clientFetch(`${process.env.REACT_APP_API_ENDPOINT}/apis/admin/companies/${id}`, {
        headers: { "Content-Type": 'application/json' },
      })

      if (!res.ok) {
        return {
          status: res.status,
          data: null,
        }
      }
      const data = await res.json();
      setCompany(data)
      return {
        status: res.status,
        data,
      }
    } catch (error) {
      console.error("Error fetching company.", error);
      return {
        status: null,
        data: null,
      }
    }
  }, [])

  // 企業IDで企業削除
  const deleteCompany = useCallback(async (id: number) => {
    try {

      const res = await clientFetch(`${process.env.REACT_APP_API_ENDPOINT}/apis/admin/companies/${id}`, {
        method: 'DELETE',
        headers: { "Content-Type": 'application/json' },
      })

      if (!res.ok) {
        throw new Error('error res')
      }

      refreshCompanies()
    } catch (error) {
      console.error("Error fetching company.")
    }
  }, [refreshCompanies])

  // 企業IDで企業ステータスを変更
  const toggleCompanyStatus = useCallback(async (id: number) => {
    try {

      await clientFetch(`${process.env.REACT_APP_API_ENDPOINT}/apis/admin/companies/${id}/toggle-disable`, {
        method: 'PATCH',
        headers: { "Content-Type": 'application/json' },
      })

      refreshCompanies()
      refreshCompany(id.toString())
    } catch (error) {
      console.error("Error toggle company.")
    }
  }, [refreshCompanies, refreshCompany])

  useEffect(() => {
    refreshCompanies()
  }, [refreshCompanies])

  return {
    companies,
    company,
    companyPagination,
    refreshCompanies,
    registerCompany,
    updateCompany,
    deleteCompany,
    refreshCompany,
    toggleCompanyStatus
  }
}

export function CompanyProvider({ children }: { children: React.ReactNode }) {
  const { companies, company, companyPagination, refreshCompanies, registerCompany, updateCompany, deleteCompany, refreshCompany, toggleCompanyStatus } = useCompanyContext()

  return (
    <UpdateCompanyContext.Provider value={{ refreshCompanies, registerCompany, updateCompany, deleteCompany, refreshCompany, toggleCompanyStatus }}>
      <CompanyContext.Provider value={{ companies, company, companyPagination }}>
        {children}
      </CompanyContext.Provider>
    </UpdateCompanyContext.Provider>
  )

}
